import { makeAutoObservable, runInAction } from "mobx";
import { fromResponseState } from "./POrderSummaryState";
import { fromResponseLoyalty, } from "./POrderSummaryLoyalty";
import { POrderInfo } from "./POrderInfo";
import { PurchaseOrderTracking } from "../../tracking";
import { PaymentRetryTimestampStorageManager, } from "../../storageBase";
import { POrderSummaryCancelled } from "./POrderSummaryCancelled";
const POLLING_INTERVAL = 15000;
const PAYMENT_RETRY_WINDOW_MS = 5 * 60 * 1000;
export class POrderSummary {
    constructor(params) {
        this.localizationProvider = params.localizationProvider;
        this.orderSummaryService = params.orderSummaryService;
        this.cartStorageManager = params.cartStorageManager;
        this.previousCartStorageManager = params.previousCartStorageManager;
        this.orderItemListStorageManager = params.orderItemListStorageManager;
        this.fulfillmentTimeStorageManager = params.fulfillmentTimeStorageManager;
        this.dialogStateManager = params.dialogStateManager;
        this.redirectToRestaurant = params.redirectToRestaurant;
        this.performPaymentRetry = params.performPaymentRetry;
        this.onPaymentRetrySuccess = params.onPaymentRetrySuccess;
        this.orderUuid = params.orderUuid;
        this.isPollingActive = false;
        this.timeout = null;
        this.paymentRetryTimeout = null;
        this._state = { type: "Loading", isFinal: false };
        this._userLoyalty = { type: "Hidden", showModal: false };
        this._orderInfo = null;
        makeAutoObservable(this);
    }
    setState(orderSummaryResponse) {
        // Assert we are not leaving final state
        if (this._state.isFinal) {
            throw new Error(`${this._state.type} is final state, refusing to transition`);
        }
        if (orderSummaryResponse === null) {
            this._state = { type: "Error", isFinal: true };
        }
        else {
            this._state = fromResponseState({
                orderSummaryResponse,
                retryOrder: () => {
                    const previousCart = this.previousCartStorageManager.restore();
                    if (previousCart !== null) {
                        this.cartStorageManager.store(previousCart);
                    }
                    this.redirectToRestaurant();
                },
                performPaymentRetry: this.performPaymentRetry,
                onPaymentRetrySuccess: this.onPaymentRetrySuccess,
            });
        }
        if (this._state instanceof POrderSummaryCancelled &&
            this._state.paymentRetryAvailable) {
            const stored = PaymentRetryTimestampStorageManager.restore();
            let startedAt;
            if (stored !== null && stored.orderUuid === this.orderUuid) {
                startedAt = stored.startedAt;
            }
            else {
                startedAt = Date.now();
                PaymentRetryTimestampStorageManager.store({
                    orderUuid: this.orderUuid,
                    startedAt,
                });
            }
            const remainingMs = startedAt + PAYMENT_RETRY_WINDOW_MS - Date.now();
            if (remainingMs <= 0) {
                runInAction(() => {
                    if (this._state instanceof POrderSummaryCancelled) {
                        this._state.setPaymentRetryExpired();
                    }
                });
            }
            else {
                const expiresAt = new Date(startedAt + PAYMENT_RETRY_WINDOW_MS);
                const expiresAtStr = expiresAt.toLocaleTimeString("pl-PL", {
                    hour: "2-digit",
                    minute: "2-digit",
                });
                console.log(`[TYP] We are in payment retry mode. Will switch to order retry at ${expiresAtStr}.`);
                this.paymentRetryTimeout = setTimeout(() => {
                    runInAction(() => {
                        if (this._state instanceof POrderSummaryCancelled) {
                            this._state.setPaymentRetryExpired();
                        }
                    });
                }, remainingMs);
            }
        }
        if (this._state.isFinal) {
            this.stopPolling();
        }
    }
    get state() {
        return this._state;
    }
    setOrderInfo(orderInfo) {
        const localizationProvider = this.localizationProvider;
        this._orderInfo = new POrderInfo({ localizationProvider, orderInfo });
    }
    get orderInfo() {
        return this._orderInfo;
    }
    setUserLoyalty(state, userLoyalty) {
        this._userLoyalty = fromResponseLoyalty(state, userLoyalty);
    }
    get userLoyalty() {
        return this._userLoyalty;
    }
    initialize() {
        // Move current cart to the previous cart for "retry" action
        const cart = this.cartStorageManager.restore();
        if (cart !== null) {
            this.previousCartStorageManager.store(cart);
        }
        // Clear order-specific data
        this.cartStorageManager.erase();
        this.orderItemListStorageManager.erase();
        this.fulfillmentTimeStorageManager.erase();
        this.dialogStateManager.state.freeDeliveryWithoutAddressBottomSheet = true;
        this.dialogStateManager.state.freeDeliveryWithAddressStickyAlert = true;
        // Set init state and start fetching data
        this._state = { type: "Loading", isFinal: false };
        this.startPolling();
    }
    dispose() {
        this.stopPolling();
        this.clearPaymentRetryTimeout();
    }
    startPolling() {
        if (this.isPollingActive === true) {
            throw new Error("Double polling initialization error");
        }
        this.isPollingActive = true;
        this.fetchOrderSummary();
    }
    fetchOrderSummary() {
        this.orderSummaryService
            .getOrderSummary(this.orderUuid)
            .then((response) => {
            runInAction(() => {
                this.setState(response);
            });
            if (response !== null) {
                runInAction(() => {
                    this.setOrderInfo(response.orderInfo);
                    this.setUserLoyalty(response.state, response.userLoyalty);
                });
                PurchaseOrderTracking.trackPurchase(this.orderUuid, response.orderInfo, response.siteName);
            }
        })
            .catch(() => {
            this.setState(null);
        })
            .finally(() => {
            if (this.isPollingActive)
                this.fetchOrderSummaryWithTimeout();
        });
    }
    fetchOrderSummaryWithTimeout() {
        this.timeout = setTimeout(() => this.fetchOrderSummary(), POLLING_INTERVAL);
    }
    stopPolling() {
        this.isPollingActive = false;
        this.clearTimeout();
    }
    clearTimeout() {
        if (this.timeout !== null) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }
    clearPaymentRetryTimeout() {
        if (this.paymentRetryTimeout !== null) {
            clearTimeout(this.paymentRetryTimeout);
            this.paymentRetryTimeout = null;
        }
    }
}
